
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSubscriptionData } from "@/hooks/useSubscriptionData";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { ArrowRight, Info } from "lucide-react";

const WelcomeDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const { data: subscription } = useSubscriptionData();
  const [userName, setUserName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsLoading(true);
      try {
        if (isDemo) {
          setUserName("Demo User");
          setIsLoading(false);
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, name')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            setUserName(profile.first_name || profile.name || "");
          }
        } else {
          navigate('/auth');
          return;
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        if (!isDemo) {
          navigate('/auth');
          return;
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [isDemo, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // For demo or testing, show a default subscription tier
  const displayTier = isDemo ? "Pro" : subscription?.tier;

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-6">
      <div className="space-y-2 text-center">
        <h1 className="text-4xl font-bold tracking-tight">
          Welcome{userName ? `, ${userName}` : ""}! 🎉
        </h1>
        <p className="text-xl text-muted-foreground">
          {isDemo ? "You're in demo mode" : `You're now subscribed to our ${displayTier} plan`}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Getting Started
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Let's begin by setting up your client directory. This will help you keep track of all your clients and generate personalized content for each one.
            </p>
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium">1. Add Your First Client</h4>
                <p className="text-muted-foreground">
                  Start by adding your first client to the directory. You'll need their basic information to get started.
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">2. Generate Content</h4>
                <p className="text-muted-foreground">
                  Once you've added a client, you can generate personalized content for them with just a few clicks.
                </p>
              </div>
            </div>
            <Button 
              className="w-full mt-4"
              onClick={() => navigate("/client-directory")}
            >
              <div className="flex items-center gap-2">
                Get Started
                <ArrowRight className="h-4 w-4" />
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WelcomeDashboard;
